import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ServicePlan, UserSubscription } from '../../../shared/types/servicePlans';
import './PlanDetailsTooltip.css';

interface PlanDetailsTooltipProps {
  plan: ServicePlan | null;
  subscription: UserSubscription | null;
}

export const PlanDetailsTooltip: React.FC<PlanDetailsTooltipProps> = ({ plan, subscription }) => {
  const navigate = useNavigate();

  // If no subscription, show loading
  if (!subscription) {
    return (
      <div className="plan-details-tooltip">
        <div className="plan-details-loading">Loading plan details...</div>
      </div>
    );
  }

  const formatPrice = (price: number): string => {
    if (price === 0) return 'Free';
    return `$${price.toFixed(2)}`;
  };


  const billingInterval = subscription.billingInterval || 'monthly';
  // Use Stripe amount as source of truth (pricing comes from Stripe, not service plan)
  const price = subscription.stripeAmount ?? null;
  
  // Get plan name - use plan.serviceName if available, otherwise map plan ID to actual name
  const getPlanNameFromId = (planId: string | undefined): string => {
    if (!planId) return 'Current Plan';
    const planNum = parseInt(planId);
    switch (planNum) {
      case 1:
        return 'Explorer';
      case 2:
        return 'Navigator';
      case 3:
        return 'Accelerator';
      case 4:
        return 'Master';
      default:
        return `Plan ${planId}`;
    }
  };
  
  const planName = plan?.serviceName ?? getPlanNameFromId(subscription.currentPlanId);

  return (
    <div className="plan-details-tooltip">
      {/* Header with animated gradient */}
      <div className="plan-details-header">
        <div className="plan-details-header-gradient"></div>
        <div className="plan-details-title">
          <span className="plan-details-icon">⭐</span>
          <span className="plan-details-name">{planName}</span>
        </div>
        {price !== null && (
          <div className="plan-details-price">
            <span className="price-amount">{formatPrice(price)}</span>
            <span className="price-period">/{billingInterval === 'monthly' ? 'month' : 'year'}</span>
          </div>
        )}
      </div>

      {/* Token Usage Section */}
      <div className="plan-details-content">
        {subscription && subscription.tokenAllowance !== undefined && (
          <div className="plan-details-section">
            <div className="plan-details-section-title">Token Usage</div>
            <div className="plan-details-tokens">
              <div className="token-item">
                <span className="token-icon">🪙</span>
                <span className="token-label">Available Tokens</span>
                <span className="token-value">{subscription.tokensRemaining ?? 0}</span>
              </div>
              <div className="token-item">
                <span className="token-icon">📊</span>
                <span className="token-label">Tokens Used</span>
                <span className="token-value">
                  {(() => {
                    const available = subscription.tokensRemaining ?? 0;
                    const allowance = subscription.tokenAllowance ?? 0;
                    // Calculate used tokens: if available > allowance, user has rollover, so used = 0 for this period
                    // Otherwise, used = allowance - available
                    if (available > allowance) {
                      return 0; // All tokens are from rollover, none used this period
                    }
                    return Math.max(0, allowance - available);
                  })()}
                </span>
              </div>
              {!subscription.isOneTimeOnly && (
                <>
                  <div className="token-item">
                    <span className="token-icon">🔄</span>
                    <span className="token-label">Next Reset</span>
                    <span className="token-value">
                      {(() => {
                        // Calculate next reset date (1st of next month at midnight UTC)
                        const now = new Date();
                        const nextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0));
                        return nextMonth.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          timeZone: 'UTC',
                        });
                      })()}
                    </span>
                  </div>
                  <div className="token-item">
                    <span className="token-icon">➕</span>
                    <span className="token-label">Next month tokens</span>
                    <span className="token-value">{subscription.tokenAllowance}</span>
                  </div>
                </>
              )}
              {subscription.isOneTimeOnly && (
                <div className="token-item token-note">
                  <span className="token-note-text">One-time plan - no token renewal</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Billing Info */}
        {subscription && (
          <div className="plan-details-section">
            <div className="plan-details-section-title">Billing</div>
            <div className="plan-details-billing">
              <div className="billing-item">
                <span className="billing-label">Billing Cycle</span>
                <span className="billing-value capitalize">{subscription.billingInterval}</span>
              </div>
              {/* Use Stripe dates as source of truth if available */}
              {(subscription.stripeCurrentPeriodEnd || subscription.nextBillingDate) && (
                <div className="billing-item">
                  <span className="billing-label">Next Billing</span>
                  <span className="billing-value">
                    {new Date(
                      subscription.stripeCurrentPeriodEnd || subscription.nextBillingDate || ''
                    ).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      timeZone: 'UTC', // Force UTC to prevent date shifting
                    })}
                  </span>
                </div>
              )}
              {/* Show current billing period from Stripe if available */}
              {subscription.stripeCurrentPeriodStart && subscription.stripeCurrentPeriodEnd && (
                <div className="billing-item">
                  <span className="billing-label">Current Period</span>
                  <span className="billing-value">
                    {new Date(subscription.stripeCurrentPeriodStart).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })} - {new Date(subscription.stripeCurrentPeriodEnd).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}
              {subscription.createdAt && (
                <div className="billing-item">
                  <span className="billing-label">Signup Date</span>
                  <span className="billing-value">
                    {new Date(subscription.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      timeZone: 'UTC', // Force UTC to prevent date shifting
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="plan-details-footer">
        <button
          type="button"
          className="plan-details-view-plans-btn"
          onClick={() => navigate('/service-plans')}
        >
          View service plans
        </button>
      </div>

      {/* Animated background particles */}
      <div className="plan-details-particles">
        {[...Array(6)].map((_, i) => (
          <div key={i} className={`particle particle-${i + 1}`}></div>
        ))}
      </div>
    </div>
  );
};

